import { type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { clearRelayConfig } from '../api/client';
import {
  IconActivity,
  IconBolt,
  IconGlobe,
  IconLogOut,
  IconSettings,
  IconUsers,
} from './Icon';

interface Props {
  children: ReactNode;
  onConfigCleared: () => void;
}

const navItems = [
  { to: '/', label: 'Accounts', icon: IconUsers, end: true },
  { to: '/warming', label: 'Warming', icon: IconActivity, end: false },
  { to: '/comment', label: 'Comment', icon: IconBolt, end: false },
  { to: '/proxies', label: 'Proxies', icon: IconGlobe, end: false },
  { to: '/settings', label: 'Settings', icon: IconSettings, end: false },
];

export function Layout({ children, onConfigCleared }: Props) {
  function disconnect() {
    if (!confirm('Forget the Worker URL and token from this browser?')) return;
    clearRelayConfig();
    onConfigCleared();
  }
  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="sticky top-0 flex h-screen w-56 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="flex h-14 items-center gap-2 px-4">
          <span className="grid h-7 w-7 place-items-center rounded-md bg-emerald-500/20 text-emerald-300 ring-1 ring-inset ring-emerald-500/30">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="10" />
            </svg>
          </span>
          <div className="flex flex-col leading-none">
            <span className="text-sm font-semibold tracking-tight">auto-reddit</span>
            <span className="mt-0.5 text-[10px] uppercase tracking-wider text-[var(--color-text-faint)]">
              warming
            </span>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 px-2 py-2">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition ${
                  isActive
                    ? 'bg-[var(--color-surface-2)] text-zinc-100'
                    : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-zinc-200'
                }`
              }
            >
              <Icon
                width={16}
                height={16}
                className="text-[var(--color-text-faint)] group-hover:text-zinc-300"
              />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[var(--color-border)] p-2">
          <button
            onClick={disconnect}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-[var(--color-text-dim)] transition hover:bg-[var(--color-surface-2)] hover:text-zinc-100"
          >
            <IconLogOut
              width={16}
              height={16}
              className="text-[var(--color-text-faint)]"
            />
            Disconnect
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 px-8 py-6">{children}</main>
    </div>
  );
}
